import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReportTemplate from '../../components/reports/ReportTemplate';
import { ROUTES } from '../../consts/general';
import { useInitialMountDelay } from '../../helpers/useInitialMountDelay';
import useDateRangeParams from '../../hooks/useDateRangeParams';

const COLUMNS = [
  { key: 'sku', label: 'Seller SKU', type: 'string' },
  { key: 'product', label: 'Product', type: 'string' },
  { key: 'marketplace', label: 'Marketplace', type: 'string' },
  { key: 'currency_code', label: 'Source Currency', type: 'string' },
  { key: 'display_currency', label: 'Amounts In', type: 'string' },
  { key: 'fx_rate', label: 'FX Rate', type: 'number' },
  { key: 'fx_doc_number', label: 'FX Journal Entry', type: 'string' },
  { key: 'gross_sales', label: 'Gross Sales', type: 'currency' },
  { key: 'returns', label: 'Returns', type: 'currency' },
  { key: 'claims_chargebacks', label: 'Claims / Chargebacks', type: 'currency' },
  { key: 'promotional_rebates', label: 'Promo Rebates', type: 'currency' },
  { key: 'shipping_giftwrap_credits', label: 'Shipping / Gift Wrap Credits', type: 'currency' },
  { key: 'net_sales', label: 'Net Product Sales', type: 'currency' },
  { key: 'net_items_sold', label: 'Units', type: 'number' },
  { key: 'cogs', label: 'COGS', type: 'currency' },
  { key: 'cogs_basis', label: 'COGS Basis', type: 'string' },
  { key: 'selling_fees', label: 'Selling Fees (Referral)', type: 'currency' },
  { key: 'fba_fees', label: 'FBA Fees', type: 'currency' },
  { key: 'other_fees', label: 'Other SKU Fees', type: 'currency' },
  { key: 'reimbursements', label: 'Reimbursements', type: 'currency' },
  { key: 'gross_profit', label: 'Gross Profit', type: 'currency' },
];

const defaultEndDate = new Date().toISOString().slice(0, 10);
const defaultStartDate = (() => {
  const date = new Date();
  date.setDate(date.getDate() - 30);
  return date.toISOString().slice(0, 10);
})();
const displayDate = (value) => {
  if (!value) return '';
  const [year, month, day] = value.split('-');
  return `${month}/${day}/${year}`;
};
const money = (value) => Number(value || 0).toLocaleString('en-US', {
  style: 'currency',
  currency: 'USD',
});

const AmazonProfitSummaryReport = ({ reportId, reportTitle, reportType, marginFromSidebar }) => {
  const navigate = useNavigate();
  const { startDate, endDate, setDateRange } = useDateRangeParams(defaultStartDate, defaultEndDate);
  const [marketplace, setMarketplace] = useState('all');
  const [skuFilter, setSkuFilter] = useState('');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [report, setReport] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const initialReady = useInitialMountDelay(500);
  const columns = useMemo(
    () => (reportType === 'product' ? COLUMNS.filter((column) => column.key !== 'sku') : COLUMNS),
    [reportType],
  );

  useEffect(() => {
    if (!initialReady || !startDate || !endDate) return;
    const controller = new AbortController();
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({
          startDate,
          endDate,
          page: String(page),
          pageSize: '50',
          marketplace,
        });
        if (skuFilter) params.set('sku', skuFilter);
        const response = await fetch(`/api/v1/reports/${reportId}?${params}`, {
          signal: controller.signal,
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        const body = await response.json();
        if (!response.ok) throw new Error(body.error || 'Unable to load Amazon Profit report');
        const result = body.data && !Array.isArray(body.data) ? body.data : body;
        setRows(Array.isArray(result.data) ? result.data : []);
        setReport(result);
      } catch (loadError) {
        if (loadError.name !== 'AbortError') setError(loadError.message);
      } finally {
        setLoading(false);
      }
    };
    load();
    return () => controller.abort();
  }, [initialReady, startDate, endDate, page, marketplace, skuFilter, reportId]);

  const filters = (
    <div className="flex flex-wrap items-end gap-3">
      <label className="text-sm text-gray-700">Start date ({displayDate(startDate)})
        <input type="date" value={startDate} onChange={(event) => setDateRange(event.target.value, endDate)} className="ml-2 rounded border px-2 py-1" />
      </label>
      <label className="text-sm text-gray-700">End date ({displayDate(endDate)})
        <input type="date" value={endDate} onChange={(event) => setDateRange(startDate, event.target.value)} className="ml-2 rounded border px-2 py-1" />
      </label>
      <label className="text-sm text-gray-700">Marketplace
        <select value={marketplace} onChange={(event) => { setMarketplace(event.target.value); setPage(1); }} className="ml-2 rounded border px-2 py-1">
          <option value="all">All marketplaces</option>
          <option value="US">US</option>
          <option value="CA">CA</option>
          <option value="MX">MX</option>
        </select>
      </label>
      {reportType === 'sku' && (
        <label className="text-sm text-gray-700">Seller SKU
          <input value={skuFilter} onChange={(event) => { setSkuFilter(event.target.value); setPage(1); }} className="ml-2 rounded border px-2 py-1" />
        </label>
      )}
    </div>
  );
  const tableRows = rows.map((row, index) => (
    <tr key={`${row.sku || row.product}-${row.marketplace}-${row.fx_doc_number}-${index}`} className={index % 2 ? 'bg-gray-50' : 'bg-white'}>
      {columns.map((column) => (
        <td key={column.key} className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
          {column.key === 'cogs_basis' && row.cogs_basis === 'virtual' ? 'Virtual ($0)' : (row[column.key] ?? (row.fx_exception ? 'FX coverage exception' : ''))}
        </td>
      ))}
    </tr>
  ));
  const reconciliation = report.reconciliation?.us || {};

  return (
    <>
      <div className="mb-3 space-y-3 text-sm text-gray-700" role="status">
        <div className="font-medium">Settlement view: Amazon posted date (Pacific). Marketing Gross in the Performance Tracker uses order date.</div>
        <div className="flex flex-wrap gap-x-5">
          {['gross_sales', 'returns', 'promotional_rebates'].map((key) => (
            <span key={key}>{key.replace('_', ' ')}: report {money(reconciliation[key]?.report)}, governed {money(reconciliation[key]?.governed)}, difference {money(reconciliation[key]?.difference)}.</span>
          ))}
          <span>Incomplete days: {report.reconciliation?.incomplete_days ?? 0}</span>
        </div>
        <div>{report.reconciliation?.untracked_claims?.label || 'Chargebacks / A-to-z claims (not in tracker returns)'}: {money(report.reconciliation?.untracked_claims?.difference)}</div>
        <div>Coverage: history from 2025-01; Jan–Mar 2025 double-count risk, pending re-verification; 2024 not certified. BRL rows: {report.coverage_notes?.brl_rows || 0}; non-Amazon US rows: {report.coverage_notes?.non_amazon_us_rows || 0}.</div>
        <div>FX table coverage: {(report.coverage_notes?.fx_table_coverage || []).map(({ marketplace, currency, first_je: firstJe, last_je: lastJe }) => `${marketplace} ${currency}: ${firstJe?.doc_number || 'n/a'} to ${lastJe?.doc_number || 'n/a'}`).join('; ') || 'none'}; affected months: {(report.coverage_notes?.fx_affected_months || []).map(({ month, currencies }) => `${month} (${currencies.map(({ row_count: rowCount, currency }) => `${rowCount} ${currency}`).join(', ')})`).join('; ') || 'none'}.</div>
      </div>
      <ReportTemplate
        reportId={reportId}
        description={reportTitle}
        marginFromSidebar={marginFromSidebar}
        onBack={() => navigate(ROUTES.REPORTS)}
        isLoading={loading}
        error={error}
        filters={filters}
        data={rows}
        columns={columns}
        pagination={
          <div className="flex items-center justify-end gap-2 p-3 text-sm">
            <button type="button" disabled={page <= 1} onClick={() => setPage((value) => value - 1)} className="rounded border px-3 py-1 disabled:opacity-50">Previous</button>
            <span>Page {page}</span>
            <button type="button" disabled={rows.length < 50} onClick={() => setPage((value) => value + 1)} className="rounded border px-3 py-1 disabled:opacity-50">Next</button>
          </div>
        }
        emptyMessage="No data found for the selected date range."
        enableSorting={false}
      >
        {tableRows}
      </ReportTemplate>
      <details className="my-4 rounded border p-3">
        <summary className="cursor-pointer font-medium">Finance bridge (separate from report totals)</summary>
        {(report.finance_bridge || []).map((month) => (
          <div key={month.month} className="mt-3">
            <div className="font-medium">{month.month}</div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead><tr><th className="text-left">Account</th><th className="text-right">Operating USD</th><th className="text-right">QBO USD</th><th className="text-right">Difference</th><th className="text-left">FX provenance</th><th className="text-left">Reason</th></tr></thead>
                <tbody>{month.lines.map((line) => (
                  <tr key={line.account}><td>{line.account} {line.label}</td><td className="text-right">{line.operating_usd === null ? '—' : money(line.operating_usd)}</td><td className="text-right">{line.qbo_usd === null ? '—' : money(line.qbo_usd)}</td><td className="text-right">{line.difference === null ? '—' : money(line.difference)}</td><td>{(line.fx_provenance || []).map((fx) => `${fx.marketplace} ${fx.currency} × ${fx.rate} (${fx.doc_number})`).join('; ') || 'USD only'}</td><td>{line.reason}</td></tr>
                ))}</tbody>
              </table>
            </div>
          </div>
        ))}
      </details>
      <details className="my-4 rounded border p-3">
        <summary className="cursor-pointer font-medium">Account-level fees ({report.account_level_fees?.length || 0} groups)</summary>
        <pre className="overflow-x-auto text-xs">{JSON.stringify(report.account_level_fees || [], null, 2)}</pre>
      </details>
      <details className="my-4 rounded border p-3">
        <summary className="cursor-pointer font-medium">FX coverage exceptions ({report.fx_coverage_exceptions?.length || 0})</summary>
        <pre className="overflow-x-auto text-xs">{JSON.stringify(report.fx_coverage_exceptions || [], null, 2)}</pre>
      </details>
      <details className="my-4 rounded border p-3">
        <summary className="cursor-pointer font-medium">COGS exceptions and unmapped SKUs</summary>
        <pre className="overflow-x-auto text-xs">{JSON.stringify({ exceptions: report.cogs_exceptions || [], unmapped: report.unmapped_skus || [] }, null, 2)}</pre>
      </details>
    </>
  );
};

export default AmazonProfitSummaryReport;
